import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ClientLayout from "@/components/ClientLayout";
import OperatorLayout from "@/components/OperatorLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Cleanings from "@/pages/Cleanings";
import CleaningDetail from "@/pages/CleaningDetail";
import Incidents from "@/pages/Incidents";
import Inventory from "@/pages/Inventory";
import Staff from "@/pages/Staff";
import Clients from "@/pages/Clients";
import Calendar from "@/pages/Calendar";
import Integrations from "@/pages/Integrations";
import Laundry from "@/pages/Laundry";
import Schedule from "@/pages/Schedule";
import Accounting from "@/pages/Accounting";
import Routines from "@/pages/Routines";
import Users from "@/pages/Users";

import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalProperties from "@/pages/portal/PortalProperties";
import PortalPropertyDetail from "@/pages/portal/PortalPropertyDetail";
import PortalCleaningMedia from "@/pages/portal/PortalCleaningMedia";
import PortalCalendar from "@/pages/portal/PortalCalendar";
import PortalIncidents from "@/pages/portal/PortalIncidents";
import PortalReports from "@/pages/portal/PortalReports";

import OperatorDashboard from "@/pages/operator/OperatorDashboard";
import OperatorTasks from "@/pages/operator/OperatorTasks";
import OperatorTaskDetail from "@/pages/operator/OperatorTaskDetail";
import OperatorSchedule from "@/pages/operator/OperatorSchedule";

import "@/App.css";

const MANAGEMENT_ROLES = ["admin", "coordinator", "supervisor", "warehouse", "manager", "super_admin"];

// Routes warehouse / oficina role CANNOT access (everything else is allowed)
const ROLE_FORBIDDEN_ROUTES = {
    warehouse: ["/usuarios", "/integraciones", "/contabilidad"],
};

const ROLE_DEFAULT_HOME = {
    warehouse: "/",
};

function defaultHomeForRole(role) {
    if (role === "client") return "/portal";
    if (role === "cleaner" || role === "technician") return "/operario";
    if (ROLE_DEFAULT_HOME[role]) return ROLE_DEFAULT_HOME[role];
    return "/";
}

function isRouteAllowedForRole(role, path) {
    const forbidden = ROLE_FORBIDDEN_ROUTES[role] || [];
    return !forbidden.some((p) => path === p || path.startsWith(p + "/"));
}

function OpShell({ children, adminOnly = false, financialOnly = false }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user && !MANAGEMENT_ROLES.includes(user.role)) {
        return <Navigate to={defaultHomeForRole(user.role)} replace />;
    }
    if (user && !isRouteAllowedForRole(user.role, location.pathname)) {
        return <Navigate to={defaultHomeForRole(user.role)} replace />;
    }
    if (user && adminOnly && user.role !== "admin" && user.role !== "super_admin") {
        return <Navigate to="/" replace />;
    }
    if (user && financialOnly && !["admin", "coordinator", "super_admin"].includes(user.role)) {
        return <Navigate to="/" replace />;
    }
    return (
        <ProtectedRoute roles={MANAGEMENT_ROLES}>
            <Layout>{children}</Layout>
        </ProtectedRoute>
    );
}

function PortalShell({ children }) {
    const { user } = useAuth();
    if (user && user.role !== "client") {
        return <Navigate to={defaultHomeForRole(user.role)} replace />;
    }
    return (
        <ProtectedRoute roles={["client"]}>
            <ClientLayout>{children}</ClientLayout>
        </ProtectedRoute>
    );
}

function OperatorShell({ children }) {
    const { user } = useAuth();
    if (user && user.role !== "cleaner" && user.role !== "technician") {
        return <Navigate to={defaultHomeForRole(user.role)} replace />;
    }
    return (
        <ProtectedRoute roles={["cleaner", "technician"]}>
            <OperatorLayout>{children}</OperatorLayout>
        </ProtectedRoute>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />

                    {/* Operational app */}
                    <Route path="/" element={<OpShell><Dashboard /></OpShell>} />
                    <Route path="/propiedades" element={<OpShell><Properties /></OpShell>} />
                    <Route path="/propiedades/:id" element={<OpShell><PropertyDetail /></OpShell>} />
                    <Route path="/calendario" element={<OpShell><Calendar /></OpShell>} />
                    <Route path="/limpiezas" element={<OpShell><Cleanings /></OpShell>} />
                    <Route path="/limpiezas/:id" element={<OpShell><CleaningDetail /></OpShell>} />
                    <Route path="/lavanderia" element={<OpShell><Laundry /></OpShell>} />
                    <Route path="/incidencias" element={<OpShell><Incidents /></OpShell>} />
                    <Route path="/almacen" element={<OpShell><Inventory /></OpShell>} />
                    <Route path="/personal" element={<OpShell><Staff /></OpShell>} />
                    <Route path="/horarios" element={<OpShell><Schedule /></OpShell>} />
                    <Route path="/rutinas" element={<OpShell><Routines /></OpShell>} />
                    <Route path="/contabilidad" element={<OpShell financialOnly><Accounting /></OpShell>} />
                    <Route path="/clientes" element={<OpShell><Clients /></OpShell>} />
                    <Route path="/usuarios" element={<OpShell adminOnly><Users /></OpShell>} />
                    <Route path="/integraciones" element={<OpShell adminOnly><Integrations /></OpShell>} />

                    {/* Client portal */}
                    <Route path="/portal" element={<PortalShell><PortalDashboard /></PortalShell>} />
                    <Route path="/portal/propiedades" element={<PortalShell><PortalProperties /></PortalShell>} />
                    <Route path="/portal/propiedades/:id" element={<PortalShell><PortalPropertyDetail /></PortalShell>} />
                    <Route path="/portal/limpiezas/:tid" element={<PortalShell><PortalCleaningMedia /></PortalShell>} />
                    <Route path="/portal/calendario" element={<PortalShell><PortalCalendar /></PortalShell>} />
                    <Route path="/portal/incidencias" element={<PortalShell><PortalIncidents /></PortalShell>} />
                    <Route path="/portal/reportes" element={<PortalShell><PortalReports /></PortalShell>} />

                    {/* Operator portal */}
                    <Route path="/operario" element={<OperatorShell><OperatorDashboard /></OperatorShell>} />
                    <Route path="/operario/tareas" element={<OperatorShell><OperatorTasks /></OperatorShell>} />
                    <Route path="/operario/tareas/:id" element={<OperatorShell><OperatorTaskDetail /></OperatorShell>} />
                    <Route path="/operario/horario" element={<OperatorShell><OperatorSchedule /></OperatorShell>} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
